// Removed drag and drop imports
import { Box, Button, Paper, TextField, Typography } from "@mui/material";
import React from "react";
import type { Item } from "../types";
import { ItemCard } from "./ItemCard";

interface ItemListProps {
	items: Item[];
	category: "good" | "bad";
	phase: "input" | "merge" | "vote" | "results";
	userId: string;
	users: Record<string, { id: string; name: string }>;
	onAddItem?: (text: string, category: "good" | "bad") => void;
	onVote?: (itemId: string) => void;
	onUpdateItem?: (itemId: string, text: string) => void;
	isOver?: boolean;
	selectionMode?: boolean;
	selectedItems?: string[];
	onItemSelect?: (itemId: string) => void;
}

export function ItemList({
	items,
	category,
	phase,
	userId,
	users,
	onAddItem,
	onVote,
	onUpdateItem,
	isOver = false,
	selectionMode = false,
	selectedItems = [],
	onItemSelect,
}: ItemListProps) {
	// Removed drag and drop functionality

	const [newItemText, setNewItemText] = React.useState("");

	const handleAddItem = () => {
		if (newItemText.trim() && onAddItem) {
			onAddItem(newItemText.trim(), category);
			setNewItemText("");
		}
	};

	const handleKeyDown = (event: React.KeyboardEvent) => {
		if (event.key === "Enter" && !event.shiftKey) {
			event.preventDefault();
			handleAddItem();
		}
	};

	const getCategoryTitle = (cat: "good" | "bad") => {
		return cat === "good" ? "What went well" : "What didn't go well";
	};

	const getCategoryColor = (cat: "good" | "bad") => {
		return cat === "good" ? "success.main" : "error.main";
	};

	const sortedItems = [...items].sort((a, b) => {
		if (phase === "results") {
			// Sort by vote count descending
			const aVotes = Array.isArray(a.votes)
				? a.votes
				: Array.from(a.votes || []);
			const bVotes = Array.isArray(b.votes)
				? b.votes
				: Array.from(b.votes || []);
			return bVotes.length - aVotes.length;
		}
		// Sort by creation time ascending
		return a.createdAt - b.createdAt;
	});

	return (
		<Paper
			sx={{
				p: 2,
				height: "100%",
				minHeight: 300,
				border: isOver ? "2px dashed #1976d2" : "1px solid #e0e0e0",
				backgroundColor: isOver ? "#f3f9ff" : "background.paper",
				transition: "all 0.2s ease",
				display: "flex",
				flexDirection: "column",
			}}
		>
			<Typography
				variant="h6"
				sx={{
					mb: 1.5,
					color: getCategoryColor(category),
					fontWeight: "bold",
				}}
			>
				{getCategoryTitle(category)}
			</Typography>

			{phase === "input" && (
				<Box sx={{ mb: 1.5 }}>
					<TextField
						fullWidth
						multiline
						rows={2}
						placeholder={`Add something that ${category === "good" ? "went well" : "didn't go well"}...`}
						value={newItemText}
						onChange={(e) => setNewItemText(e.target.value)}
						onKeyDown={handleKeyDown}
						sx={{ mb: 0.5 }}
					/>
					<Button
						variant="contained"
						onClick={handleAddItem}
						disabled={!newItemText.trim()}
						size="small"
					>
						Add Item
					</Button>
				</Box>
			)}

			<Box
				sx={{
					flex: 1,
					overflowY: "auto",
					pr: 1,
				}}
			>
				{sortedItems.length === 0 ? (
					<Typography
						variant="body2"
						color="text.secondary"
						sx={{ fontStyle: "italic", textAlign: "center", py: 4 }}
					>
						No items yet.{" "}
						{phase === "input"
							? "Add some items above!"
							: "Items will appear here."}
					</Typography>
				) : (
					sortedItems.map((item) => {
						// Get author names for merged items
						const authorNames = item.authorIds
							? item.authorIds
									.map((authorId) => users[authorId]?.name)
									.filter(Boolean)
							: undefined;

						return (
							<ItemCard
								key={item.id}
								item={item}
								category={category}
								phase={phase}
								userId={userId}
								authorName={users[item.authorId]?.name}
								authorNames={authorNames}
								users={users}
								onVote={onVote}
								onUpdateItem={onUpdateItem}
								selectionMode={selectionMode}
								isSelected={selectedItems.includes(item.id)}
								onSelect={onItemSelect}
							/>
						);
					})
				)}
			</Box>
		</Paper>
	);
}
